<template>
    <fieldset
        class="mt-2 mb-3 avoid-page-break-inside"
        data-test-role="pv-array-div">
        <legend class="h6 mb-0">
            <span class="d-inline-flex align-items-baseline col-12 col-lg-9 mb-0">
                <div class="mb-2">
                    <span>{{ pvArraySectionLabel }}</span>
                    <pc-button
                        v-bind:disabled="readonly"
                        class="mx-4 p-0 border-0"
                        style="line-height: inherit"
                        data-test-role="delete-array"
                        link
                        v-on:click="onDelete">
                        {{ deleteArrayButtonText }}
                    </pc-button>
                </div>
            </span>
        </legend>
        <div v-if="arrayDescriptionEnabled">
            <div class="row gx-3 mb-3">
                <pc-input
                    ref="descriptionInput"
                    v-model="description"
                    v-bind:maxlength="2048"
                    class="col-9"
                    v-bind:placeholder="pvArrayDescriptionPlaceholder"
                    v-bind:readonly="readonly"
                    lazy />
            </div>
        </div>
        <model-picker
            ref="modelPicker"
            class="mb-2"
            v-bind:manufacturers="pvModuleManufacturers"
            v-bind:load-models="loadPvModuleModels"
            v-bind:spec="pvModule"
            v-bind:parent-id="parentId"
            v-bind:equipment-type="equipmentType"
            v-bind:can-copy="canCopy"
            v-bind:readonly="readonly"
            v-on:copy-component="onCopyModuleGroupClicked" />
        <transition
            v-bind:css="false"
            v-on:enter="enter"
            v-on:leave="leave">
            <div v-if="showModuleDetails">
                <div class="row gx-3 mb-2">
                    <div class="col-12">
                        <pc-alert
                            v-model:shown="trackingHelpShown"
                            v-bind:severity="helpAlertSeverity"
                            class="col-9"
                            dismissible>
                            {{ trackingHelpText }}
                        </pc-alert>
                    </div>
                    <div
                        v-if="showTiltAzimuth"
                        v-bind:class="responsiveClasses.tiltAzimuthInput">
                        <pc-input
                            ref="tiltInput"
                            v-model="tilt"
                            v-bind:validator="tiltValidator"
                            v-bind:show-validation-errors="false"
                            v-bind:validation-enabled="pvSystemElement.validationEnabled"
                            v-bind:readonly="readonly"
                            v-bind:label="tiltLabel"
                            inputmode="numeric"
                            data-test-role="tilt-input"
                            lazy
                            v-on:validated="onValidated('tilt', $event)" />
                        <small class="form-text text-body-secondary">
                            {{ tiltBoundaries }}
                        </small>
                    </div>
                    <div
                        v-if="showTiltAzimuth"
                        v-bind:class="responsiveClasses.tiltAzimuthInput">
                        <pc-input
                            ref="azimuthInput"
                            v-model="azimuth"
                            v-bind:validator="azimuthValidator"
                            v-bind:show-validation-errors="false"
                            v-bind:validation-enabled="pvSystemElement.validationEnabled"
                            v-bind:readonly="readonly"
                            v-bind:label="azimuthLabel"
                            inputmode="numeric"
                            data-test-role="azimuth-input"
                            lazy
                            delay-validation
                            v-on:validated="onValidated('azimuth', $event)" />
                        <small class="form-text text-body-secondary">
                            {{ azimuthBoundaries }}
                        </small>
                    </div>
                    <div v-bind:class="responsiveClasses.trackingInput">
                        <pc-select
                            v-model="tracking"
                            v-bind:readonly="readonly"
                            v-bind:label="trackingLabel"
                            data-test-role="tracking-select"
                            show-help
                            v-on:help-clicked="trackingHelpShown = !trackingHelpShown">
                            <option value="Fixed">
                                {{ fixedTrackingOption }}
                            </option>
                            <option value="SingleAxis">
                                {{ singleAxisTrackingOption }}
                            </option>
                            <option value="DualAxis">
                                {{ dualAxisTrackingOption }}
                            </option>
                        </pc-select>
                    </div>
                    <validation-render-helper
                        v-bind:validation-error="tiltAzimuthValidationErrors"
                        class="mt-0" />
                </div>
                <div
                    v-if="shadingEnabled"
                    class="row gx-3">
                    <div
                        v-bind:class="responsiveClasses.shadingSubtitleWrapper"
                        class="align-items-center flex-column">
                        {{ shadingLabel }}
                        <div class="form-text text-body-secondary row gx-3 w-100">
                            <div
                                v-for="shadingSubtitle in shadingSubtitles"
                                v-bind:key="shadingSubtitle"
                                v-bind:class="responsiveClasses.shadingSubtitleInput"
                                class="justify-content-center">
                                <small>
                                    {{ shadingSubtitle }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div v-bind:class="responsiveClasses.shadingWrapper">
                        <div class="row gx-3">
                            <div
                                v-for="(shading, index) in shadings"
                                ref="shading"
                                v-bind:key="index"
                                v-bind:class="responsiveClasses.shadingInput"
                                class="px-1 d-flex flex-column align-items-center">
                                <label
                                    class="m-0"
                                    v-bind:for="shading.id">
                                    <small>{{ shading.title }}</small>
                                </label>
                                <pc-input
                                    v-bind:id="shading.id"
                                    class="w-100"
                                    v-bind:model-value="shading.value"
                                    v-bind:validator="shadingValidator"
                                    v-bind:show-validation-errors="false"
                                    v-bind:validation-enabled="pvSystemElement.validationEnabled"
                                    v-bind:readonly="readonly"
                                    inputmode="decimal"
                                    data-test-role="shading-input"
                                    lazy
                                    v-on:update:model-value="onShadingInput(index, $event)"
                                    v-on:validated="onShadingValidated(index, $event)" />
                            </div>
                            <validation-render-helper
                                v-bind:validation-error="shadingValidationErrors"
                                class="mt-0" />
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </fieldset>
</template>

<script>
    import modelPicker from "./modelPicker.vue";
    import { SLIDE_DOWN_UP } from "Util/animations";
    import { ALERT_SEVERITY } from "Util/alerts";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./modulegroup.i18n";
    import { extendManufacturerList, extendModelList } from "./_modelsUtil";
    import { COMPONENT_TYPE } from "Types/componentTypes";
    import { PV_SYSTEM_INPUT_BOUNDS } from "Forms/Components/ProjectForm/ProjectElements/pvSystemElementController";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        tiltAzimuthInput: {
            // col-6 col-sm-3 col-md-2 col-lg-1
            col: { xs: 6, sm: 3, md: 2, lg: 1 }
        },
        trackingInput: {
            // col-12 col-sm-6 col-md-3
            col: { xs: 12, sm: 6, md: 3 }
        },
        shadingSubtitleWrapper: {
            // col-12 col-xl-2 d-xl-flex
            col: { xs: 12, xl: 2 },
            d: { xl: "flex" }
        },
        shadingSubtitleInput: {
            // col-auto col-xl-12 d-inline d-xl-flex
            col: { xs: "auto", xl: 12 },
            d: { xs: "inline", xl: "flex" }
        },
        shadingWrapper: {
            // col-12 col-lg-8
            col: { xs: 12, lg: 8 }
        },
        shadingInput: {
            // col-3 col-sm-2 col-md-1
            col: { xs: 3, sm: 2, md: 1 }
        }
    };

    function mapComputeds(propNames) {
        const props = {};
        propNames.forEach((name) => {
            props[name] = {
                get() {
                    return this.pvModule[name];
                },
                set(value) {
                    this.pvSystemElement.setPvModuleProperty({
                        subsystemId: this.parentId,
                        pvModuleId: this.pvModule.id,
                        name,
                        value
                    });
                }
            };
        });
        return props;
    }

    // The server-side code requires a tilt and azimuth value, even for dual-axis tracking systems
    //  for which they are irrelevant -- use this placeholder value to satisfy it
    const dualAxisTiltAndAzimuthPlaceholder = "0";

    export default {
        components: { modelPicker, validationRenderHelper },
        inject: {
            pvSystemElement: {
                from: "pvSystemElement"
            },
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            pvModule: { type: Object, required: true },
            parentId: { type: String, required: true },
            readonly: { type: Boolean, required: false, default: false }
        },
        emits: ["copy-modulegroup"],
        data() {
            return {
                equipmentType: COMPONENT_TYPE.PV_Module,
                canCopy: true, // PV Modules can always be copied
                pvModuleModels: [],
                modelsLoading: false,
                showModuleDetails: Boolean(this.pvModule.modelId),
                trackingHelpShown: false,
                helpAlertSeverity: ALERT_SEVERITY.info,
                validationResults: {
                    tilt: null,
                    azimuth: null,
                    shading: []
                }
            };
        },
        computed: {
            showTiltAzimuth() {
                return this.pvModule.tracking != "DualAxis";
            },
            tiltValidator() {
                const tiltValidatorOptions = [
                    { type: VALIDATOR_TYPES.required, overrideMsg: this.tiltNotInputMessage },
                    { type: VALIDATOR_TYPES.integer, overrideMsg: this.tiltNotAnIntegerMessage },
                    {
                        type: VALIDATOR_TYPES.min,
                        min: PV_SYSTEM_INPUT_BOUNDS.MinTilt,
                        overrideMsg: this.tiltOutOfRangeMessage
                    },
                    {
                        type: VALIDATOR_TYPES.max,
                        max: PV_SYSTEM_INPUT_BOUNDS.MaxTilt,
                        overrideMsg: this.tiltOutOfRangeMessage
                    }
                ];

                return getValidator(this.$app, tiltValidatorOptions);
            },
            azimuthValidator() {
                const azimuthValidatorOptions = [
                    { type: VALIDATOR_TYPES.required, overrideMsg: this.azimuthNotInputMessage },
                    { type: VALIDATOR_TYPES.integer, overrideMsg: this.azimuthNotAnIntegerMessage },
                    // eslint-disable-next-line max-len
                    {
                        type: VALIDATOR_TYPES.min,
                        min: PV_SYSTEM_INPUT_BOUNDS.MinAzimuth,
                        overrideMsg: this.azimuthOutOfRangeMessage
                    },
                    // eslint-disable-next-line max-len
                    {
                        type: VALIDATOR_TYPES.max,
                        max: PV_SYSTEM_INPUT_BOUNDS.MaxAzimuth,
                        overrideMsg: this.azimuthOutOfRangeMessage
                    }
                ];

                return getValidator(this.$app, azimuthValidatorOptions);
            },
            tiltAzimuthValidationErrors() {
                const validationErrors = [];

                if (this.validationResults.tilt && this.validationResults.tilt.hasError) {
                    validationErrors.push(this.validationResults.tilt.error);
                }

                if (this.validationResults.azimuth && this.validationResults.azimuth.hasError) {
                    validationErrors.push(this.validationResults.azimuth.error);
                }

                return validationErrors;
            },
            monthlyShadingValidator() {
                const monthlyShadingValidatorOptions = [
                    {
                        type: VALIDATOR_TYPES.min,
                        min: PV_SYSTEM_INPUT_BOUNDS.MinMonthlyShading,
                        overrideMsg: this.monthlyShadingOutOfRangeMessage
                    },
                    {
                        type: VALIDATOR_TYPES.max,
                        max: PV_SYSTEM_INPUT_BOUNDS.MaxMonthlyShading,
                        overrideMsg: this.monthlyShadingOutOfRangeMessage
                    }
                ];
                return getValidator(this.$app, monthlyShadingValidatorOptions);
            },
            azimuthalShadingValidator() {
                const azimuthalShadingValidatorOptions = [
                    {
                        type: VALIDATOR_TYPES.min,
                        min: PV_SYSTEM_INPUT_BOUNDS.MinAzimuthalShading,
                        overrideMsg: this.nonmonthlyShadingRangeMessage
                    },
                    {
                        type: VALIDATOR_TYPES.max,
                        max: PV_SYSTEM_INPUT_BOUNDS.MaxAzimuthalShading,
                        overrideMsg: this.nonmonthlyShadingRangeMessage
                    }
                ];
                return getValidator(this.$app, azimuthalShadingValidatorOptions);
            },
            shadingValidator() {
                return this.pvSystemElement.isMonthlyShading
                    ? this.monthlyShadingValidator
                    : this.azimuthalShadingValidator;
            },
            shadingValidationErrors() {
                const firstResultWithError = this.validationResults.shading.find((result) => result && result.hasError);

                if (firstResultWithError) {
                    // for now, all shading errors are the same,
                    // so if we have even one, we can just return one message

                    return firstResultWithError.error;
                }
                return null;
            },
            shadingEnabled() {
                return !this.$serviceControllers.PvSystem.HideShadingInfo;
            },
            arrayDescriptionEnabled() {
                return this.$serviceControllers.PvSystem.DisplayArrayDescription;
            },
            hasModuleModel() {
                return Boolean(this.pvModule.modelId);
            },
            shadingSubtitles() {
                if (this.pvSystemElement.isMonthlyShading) {
                    return [this.shadingMonthlySubtitleHeader, this.shadingMonthlySubtitleExplanatory];
                } else {
                    // \u00B0 === °, but there are several very similar symbols, so just use the code directly
                    return [this.shadingNonmonthlySubtitleHeader, this.shadingNonmonthlySubtitleValidRange];
                }
            },
            shadings() {
                let shadingLabels;
                if (this.pvSystemElement.isMonthlyShading) {
                    shadingLabels = this.$app.DateCultureMessageInfo.AbbreviatedMonthsOfTheYear.filter((v) =>
                        Boolean(v)
                    );
                } else {
                    shadingLabels = ["E (90)", "120", "150", "S (180)", "210", "240", "W (270)"];
                }
                return this.pvModule.shading.map((value, index) => ({
                    title: shadingLabels[index],
                    value,
                    id: `${this.parentId}Array${this.pvModule.index}Shading${index}`
                }));
            },
            pvModuleManufacturers() {
                const pvManufList = this.$serviceControllers.PvSystem.getPvModuleManufacturers() || [];

                if (this.pvModule && this.pvModule.manufacturerId && parseInt(this.pvModule.manufacturerId) >= 0) {
                    const clearManufId = extendManufacturerList(
                        pvManufList,
                        this.pvModule.manufacturerId,
                        this.pvSystemElement.projectElement.AuxiliaryModel.storedPvModels
                    );

                    if (clearManufId) {
                        this.pvSystemElement.setPvModuleProperty({
                            subsystemId: this.parentId,
                            pvModuleId: this.pvModule.id,
                            name: "manufacturerId",
                            value: ""
                        });
                    }
                }

                return pvManufList;
            },
            ...mapComputeds(["tilt", "azimuth", "tracking", "description"]),
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            hasModuleModel(value) {
                if (!this.showModuleDetails && value) {
                    this.showModuleDetails = true;
                }
            },
            showTiltAzimuth(value) {
                const newTiltAndAzimuth = value ? "" : dualAxisTiltAndAzimuthPlaceholder;
                this.pvSystemElement.setPvModuleProperty({
                    subsystemId: this.parentId,
                    pvModuleId: this.pvModule.id,
                    name: "tilt",
                    value: newTiltAndAzimuth
                });
                this.pvSystemElement.setPvModuleProperty({
                    subsystemId: this.parentId,
                    pvModuleId: this.pvModule.id,
                    name: "azimuth",
                    value: newTiltAndAzimuth
                });
                if (!value) {
                    this.validationResults.tilt = null;
                    this.validationResults.azimuth = null;
                }
            }
        },
        methods: {
            validate() {
                let hasError = this.$refs.tiltInput.validate();
                hasError = hasError || this.$refs.azimuthInput.validate();
                return hasError;
            },
            onValidated(prop, validationResult) {
                this.validationResults[prop] = validationResult;
            },
            onShadingValidated(index, validationResult) {
                // Vue can't track items being added to arrays through the index directly.
                // however, we can modify a shallow copy and set it to the property vue is tracking.
                const temp = this.validationResults.shading.slice();
                temp[index] = validationResult;
                this.validationResults.shading = temp;
            },
            onShadingInput(index, value) {
                // Remove any commas that might appear in the value input -- they will corrupt the shading array
                value = value.replace(/,/g, "");
                this.pvSystemElement.setPvModuleShading({
                    subsystemId: this.parentId,
                    pvModuleId: this.pvModule.id,
                    index,
                    value
                });
            },
            onDelete() {
                this.pvSystemElement.deleteModule({
                    subsystemId: this.parentId,
                    pvModuleId: this.pvModule.id
                });
            },
            async loadPvModuleModels(manufacturerId) {
                const pvModelList = (await this.$serviceControllers.PvSystem.getPvModuleModels(manufacturerId)) || [];

                if (this.pvModule && this.pvModule.modelId && parseInt(this.pvModule.modelId) >= 0) {
                    extendModelList(
                        pvModelList,
                        this.pvModule.modelId,
                        this.pvSystemElement.projectElement.AuxiliaryModel.storedPvModels,
                        this.updatedModelSuffix
                    );
                }

                return pvModelList;
            },
            async onCopyModuleGroupClicked() {
                await this.$emit("copy-modulegroup", this.pvModule.id);
            },
            focusModuleGroupInput() {
                if (this.arrayDescriptionEnabled) {
                    this.$refs.descriptionInput.focusIn();
                } else {
                    this.$refs.modelPicker.setFocusToQuantityInput();
                }
            },
            enter: SLIDE_DOWN_UP.enter,
            leave: SLIDE_DOWN_UP.leave
        }
    };
</script>
